/*!
 * Copyright © 2025-2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, Signal, SimpleChanges } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { isElementNull } from '../../../../utils';
import { DeviceHealthIssueCategory, EPlotType } from '../../../core/models';
import { AccountFeatureToggle } from '../../../core/constants/account-feature-toggle.constant';
import { AccountFeatureToggleService } from '../../../core/services/account-feature-toggle/account-feature-toggle.service';
import { isValidEmbeddedTarget, withEmbeddedTarget } from '../../../core/utils/url.utils';
import { IDeviceHealth } from '../../../core/store/state';

@Component({
  selector: 'fm-device-health-balloon-section',
  templateUrl: './device-health-balloon-section.component.html',
  styleUrls: ['./device-health-balloon-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceHealthBalloonSectionComponent implements OnChanges {
  @Input()
  public vehicleHealthStatus: IDeviceHealth;
  @Input()
  public hasPermissionForCameras: boolean;
  @Input()
  public hasPermissionToViewHealthStatus: boolean;
  @Input()
  public deviceType: EPlotType;

  public healthCategoryCss: string;
  public showComponentHealthMultipleError: boolean;
  public showCameraHealthMultipleError: boolean;
  public cameraTroubleshootLink: string;
  public trackerTroubleshootLink: string;

  private readonly _hubEnabled: Signal<boolean>;

  constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    accountFeatureToggle: AccountFeatureToggleService
  ) {
    this._hubEnabled = accountFeatureToggle.isEnabled(AccountFeatureToggle.CentralizedAccountHubEnabled);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.vehicleHealthStatus) {
      this.healthCategoryCss = this._getHealthStatusCss();
      this.showCameraHealthMultipleError = this._showMultipleCameraHealthError();
      this.showComponentHealthMultipleError = this._showMultipleComponentHealthError();
      this.cameraTroubleshootLink = this._getTroubleshootingLinkForCamera();
      this.trackerTroubleshootLink = this._getTroubleshootingLinkForTracker();
    }
  }

  onTrackerTroubleshoot() {
    this._document.defaultView.window.open(this.trackerTroubleshootLink);
  }

  onCameraTroubleshoot() {
    this._document.defaultView.window.open(this.cameraTroubleshootLink);
  }

  private _getHealthStatusCss(): string {
    return this.vehicleHealthStatus?.healthStatus.toUpperCase() == DeviceHealthIssueCategory.Critical
      ? 'device-health-status__container__health-header__status__critical'
      : this.vehicleHealthStatus?.healthStatus.toUpperCase() == DeviceHealthIssueCategory.Major
      ? 'device-health-status__container__health-header__status__major'
      : this.vehicleHealthStatus?.healthStatus.toUpperCase() == DeviceHealthIssueCategory.Moderate
      ? 'device-health-status__container__health-header__status__moderate'
      : '';
  }

  private _getTroubleshootingLinkForTracker(): string {
    const vehicleId: number = Number.parseInt(this.vehicleHealthStatus?.vehicleId);
    const encoded = this._encode({
      fleetId: vehicleId,
      fleetType: this._getHostTypeForTroubleshoot(this.vehicleHealthStatus?.hostType),
      deviceId: this.vehicleHealthStatus?.componentHealth[0]?.DeviceId
    });
    const trackerBaseUrl = environment.config.myAccountHost?.trackerTroubleshootBaseUrl;
    if (this._hubEnabled() && isValidEmbeddedTarget(trackerBaseUrl)) {
      return withEmbeddedTarget(trackerBaseUrl, `${encoded}/device-details`);
    }
    return `/fleet/${encoded}/device-details`;
  }

  private _getHostTypeForTroubleshoot(hostType: string): string {
    if (isElementNull(hostType)) return '';
    if (hostType.toUpperCase() == 'POWERED ASSET') return 'PoweredAsset';
    if (hostType.toUpperCase() == 'NON-POWERED ASSET') return 'NonPoweredAsset';
    return hostType;
  }

  private _getTroubleshootingLinkForCamera(): string {
    const vehicleId: number = Number.parseInt(this.vehicleHealthStatus?.vehicleId);
    const encoded = this._encode({
      vehicleId: vehicleId,
      controlUnitIds: this.vehicleHealthStatus?.cameraHealth.map(x => x.ControlUnitIds)[0]
    });
    const cameraBaseUrl = environment.config.myAccountHost?.cameraListBaseUrl;
    if (this._hubEnabled() && isValidEmbeddedTarget(cameraBaseUrl)) {
      return withEmbeddedTarget(cameraBaseUrl, encoded);
    }
    return `/integratedvideo/cameras/${encoded}`;
  }

  private _showMultipleComponentHealthError(): boolean {
    return this.vehicleHealthStatus?.componentHealth.length > 1;
  }

  private _showMultipleCameraHealthError(): boolean {
    return this.vehicleHealthStatus?.cameraHealth.length > 1;
  }

  private _encode(data: Record<string, unknown>): string {
    let stringToEncode = JSON.stringify(data);
    return this._getBase64StringWithoutPadding(this._document.defaultView.btoa(stringToEncode));
  }

  private _getBase64StringWithoutPadding(url: string): string {
    if (url.endsWith('=')) {
      return this._getBase64StringWithoutPadding(url.slice(0, url.length - 1));
    }
    return url;
  }
}
